import { CalendarRange, Download, RefreshCw, Scale, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { BarChart } from '@/components/charts/bar-chart';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, SectionCard } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { CampoFecha } from '@/components/ui/calendario';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { exportarCsv } from '@/lib/export';
import { cn, formatCurrency, formatDateTimeValue } from '@/lib/utils';

import { MonitoreoNav } from '../components/monitoreo-nav';
import { COLOR_DISCREPANCIA, galones, tonoDiscrepancia } from '../lib/monitoreo-live';
import { aApi, aLocal } from '../lib/pts2-format';
import { useConciliacion, useControllerSetup } from '../monitoreo.api';
import type { FiltrosRango } from '../monitoreo.types';

const SELECT_CLASSES =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 transition-colors ' +
  'focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const INPUT_CLASSES = SELECT_CLASSES;

/** Rango por defecto: el día de hoy (el reporte se consulta al cierre del día). */
function rangoInicial(): FiltrosRango {
  const ahora = new Date();
  const inicio = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate(), 0, 0);

  return { tank: '0', fecha_inicio: aLocal(inicio), fecha_fin: aLocal(ahora) };
}

/** Tarjeta de indicador de la conciliación. */
function Indicador({
  titulo,
  valor,
  detalle,
  className,
}: {
  titulo: string;
  valor: string;
  detalle?: string;
  className?: string;
}) {
  return (
    <Card className="px-4 py-4">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">{titulo}</p>
      <p className={cn('mt-1 text-xl font-bold tabular-nums text-ink-900', className)}>{valor}</p>
      {detalle ? <p className="mt-0.5 text-xs text-ink-500">{detalle}</p> : null}
    </Card>
  );
}

/**
 * Conciliación surtidores / tanques (`/monitoreo/conciliacion`).
 *
 * Puerto de `Reportes::conciliacion_surtidores()`: compara lo despachado por
 * los surtidores (transacciones en la base de datos) con la disminución medida
 * por las sondas, y muestra la discrepancia en galones y en porcentaje.
 */
export function ConciliacionPage() {
  useDocumentTitle('Monitoreo · Conciliación surtidores');

  const [filtros, setFiltros] = useState<FiltrosRango>(() => rangoInicial());

  const setup = useControllerSetup();
  const reporte = useConciliacion(filtros);

  const tanques = setup.data?.tanques ?? [];
  const datos = reporte.data;

  const totales = datos?.totales;
  const tono = tonoDiscrepancia(totales?.discrepancy_percent ?? 0);

  /** Barras comparativas por tanque: despachado vs disminución medida. */
  const grafico = useMemo(() => {
    const filas = datos?.tanks_data ?? [];

    const despachadoPorTanque = new Map<number, number>();
    for (const transaccion of datos?.pump_transactions ?? []) {
      despachadoPorTanque.set(
        transaccion.Tank,
        (despachadoPorTanque.get(transaccion.Tank) ?? 0) + transaccion.Volume,
      );
    }

    return {
      labels: filas.map((fila) => `T${fila.tank_id} ${fila.name}`),
      series: [
        {
          label: 'Disminución en tanque',
          data: filas.map((fila) => fila.decrease),
          color: '#0ea5e9',
        },
        {
          label: 'Despachado por surtidores',
          data: filas.map((fila) => despachadoPorTanque.get(fila.tank_id) ?? 0),
          color: '#6366f1',
        },
      ],
    };
  }, [datos]);

  const exportar = () => {
    if (!datos) return;

    exportarCsv(
      'conciliacion-surtidores',
      ['Tanque', 'Producto', 'Mediciones', 'Volumen inicial', 'Volumen final', 'Disminución'],
      datos.tanks_data.map((fila) => [
        fila.tank_id,
        fila.name,
        fila.count,
        fila.volume_start.toFixed(3),
        fila.volume_end.toFixed(3),
        fila.decrease.toFixed(3),
      ]),
    );
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Monitoreo y control
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Scale className="size-6 text-brand-500" aria-hidden="true" />
            Conciliación surtidores - tanques
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-500">
            Compara los galones despachados por los surtidores con la disminución medida por las
            sondas de los tanques en el mismo período.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void reporte.refetch()}
            loading={reporte.isFetching}
          >
            <RefreshCw className="size-3.5" aria-hidden="true" />
            Actualizar
          </Button>
          <Button variant="outline" size="sm" onClick={exportar} disabled={!datos}>
            <Download className="size-3.5" aria-hidden="true" />
            Exportar
          </Button>
        </div>
      </header>

      <MonitoreoNav active="conciliacion" />

      <SectionCard title="Filtros" description="Tanque y rango de fechas a conciliar.">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex min-w-[12rem] flex-col gap-1 text-[11px] font-medium text-ink-500">
            Tanque
            <Select
              value={filtros.tank}
              onChange={(event) => setFiltros((prev) => ({ ...prev, tank: event.target.value }))}
              className={SELECT_CLASSES}
            >
              <option value="0">Todos los tanques</option>
              {tanques.map((tanque) => (
                <option key={tanque.id_tank} value={String(tanque.id_tank)}>
                  Tanque {tanque.id_tank} · {tanque.producto}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex min-w-[13rem] flex-col gap-1 text-[11px] font-medium text-ink-500">
            Desde
            <CampoFecha
             
              value={filtros.fecha_inicio}
              onChange={(event) =>
                setFiltros((prev) => ({ ...prev, fecha_inicio: event.target.value }))
              }
              className={INPUT_CLASSES}
             tipo="datetime-local" />
          </label>

          <label className="flex min-w-[13rem] flex-col gap-1 text-[11px] font-medium text-ink-500">
            Hasta
            <CampoFecha
             
              value={filtros.fecha_fin}
              onChange={(event) => setFiltros((prev) => ({ ...prev, fecha_fin: event.target.value }))}
              className={INPUT_CLASSES}
             tipo="datetime-local" />
          </label>

          <Button
            size="sm"
            onClick={() => void reporte.refetch()}
            loading={reporte.isFetching}
            className="mb-px"
          >
            <Search className="size-3.5" aria-hidden="true" />
            Consultar
          </Button>

          <p className="mb-2 flex items-center gap-1.5 text-xs text-ink-500">
            <CalendarRange className="size-3.5" aria-hidden="true" />
            {aApi(filtros.fecha_inicio) || 'sin fecha'} → {aApi(filtros.fecha_fin) || 'sin fecha'}
          </p>
        </div>
      </SectionCard>

      {datos?.aviso ? (
        <Alert variant="warning" title="No se pudo leer los tanques">
          {datos.aviso} El total despachado sí corresponde a las transacciones registradas.
        </Alert>
      ) : null}

      {reporte.isPending ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((clave) => (
              <Skeleton key={clave} className="h-24 rounded-card" />
            ))}
          </div>
          <Skeleton className="h-72 rounded-card" />
        </div>
      ) : reporte.isError ? (
        <Alert variant="error" title="No se pudo conciliar">
          <div className="flex flex-wrap items-center gap-3">
            <span>Verifica el tanque y el rango de fechas, e inténtalo nuevamente.</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void reporte.refetch()}
              loading={reporte.isFetching}
            >
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Indicador
              titulo="Total despachado"
              valor={`${galones(totales?.total_pump)} gal`}
              detalle={`${datos?.pump_transactions.length ?? 0} transacciones`}
            />
            <Indicador
              titulo="Disminución en tanques"
              valor={`${galones(totales?.tank_decrease)} gal`}
              detalle={`${datos?.tanks_data.length ?? 0} tanque(s) con mediciones`}
            />
            <Indicador
              titulo="Discrepancia"
              valor={`${galones(totales?.discrepancy)} gal`}
              detalle="Despachado − disminución"
            />
            <Indicador
              titulo="Discrepancia porcentual"
              valor={`${galones(totales?.discrepancy_percent)} %`}
              detalle="Hasta 1 %: normal · hasta 3 %: revisar"
              className={COLOR_DISCREPANCIA[tono]}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <SectionCard
              className="min-w-0"
              title="Disminución medida vs. despacho"
              description="Por tanque, en galones."
            >
              {grafico.labels.length === 0 ? (
                <p className="py-10 text-center text-sm text-ink-500">
                  Sin mediciones de tanque en el período.
                </p>
              ) : (
                <BarChart
                  labels={grafico.labels}
                  series={grafico.series}
                  height={260}
                  format="number"
                />
              )}
            </SectionCard>

            <SectionCard
              className="min-w-0"
              title="Resumen por tanque"
              description="Volumen medido al inicio y al final del período."
            >
              <div className="overflow-x-auto">
                <div className="min-w-[34rem]">
                  <div className="grid grid-cols-[5rem_minmax(0,1fr)_6rem_7.5rem_7.5rem_7.5rem] items-center gap-x-3 border-b border-ink-100 pb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                    <span>Tanque</span>
                    <span>Producto</span>
                    <span className="text-right">Medic.</span>
                    <span className="text-right">Inicial</span>
                    <span className="text-right">Final</span>
                    <span className="text-right">Disminución</span>
                  </div>

                  {(datos?.tanks_data ?? []).map((fila) => (
                    <div
                      key={fila.tank_id}
                      className="grid grid-cols-[5rem_minmax(0,1fr)_6rem_7.5rem_7.5rem_7.5rem] items-center gap-x-3 border-b border-ink-50 py-2 text-sm text-ink-800"
                    >
                      <span className="font-semibold">#{fila.tank_id}</span>
                      <span className="truncate">{fila.name}</span>
                      <span className="text-right tabular-nums">{fila.count}</span>
                      <span className="text-right tabular-nums">{galones(fila.volume_start)}</span>
                      <span className="text-right tabular-nums">{galones(fila.volume_end)}</span>
                      <span className="text-right font-semibold tabular-nums">
                        {galones(fila.decrease)}
                      </span>
                    </div>
                  ))}

                  {(datos?.tanks_data ?? []).length === 0 ? (
                    <p className="py-6 text-center text-sm text-ink-500">
                      Sin mediciones de tanque en el período.
                    </p>
                  ) : null}
                </div>
              </div>
            </SectionCard>
          </div>

          <SectionCard
            title="Transacciones despachadas"
            description="Detalle de lo vendido por los surtidores en el período."
            action={
              <Badge variant="outline">
                {datos?.pump_transactions.length ?? 0} transacción(es)
              </Badge>
            }
          >
            <div className="max-h-[26rem] overflow-auto">
              <div className="min-w-[52rem]">
                <div className="sticky top-0 grid grid-cols-[3.5rem_11rem_6rem_7rem_minmax(0,1fr)_7.5rem_7.5rem_7.5rem] items-center gap-x-3 border-b border-ink-100 bg-white pb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                  <span>#</span>
                  <span>Fecha y hora</span>
                  <span className="text-right">Lado</span>
                  <span className="text-right">Manguera</span>
                  <span>Producto</span>
                  <span className="text-right">Volumen</span>
                  <span className="text-right">Precio</span>
                  <span className="text-right">Importe</span>
                </div>

                {(datos?.pump_transactions ?? []).slice(0, 500).map((fila, indice) => (
                  <div
                    key={`${fila.DateTime}-${fila.Pump}-${fila.Nozzle}-${indice}`}
                    className="grid grid-cols-[3.5rem_11rem_6rem_7rem_minmax(0,1fr)_7.5rem_7.5rem_7.5rem] items-center gap-x-3 border-b border-ink-50 py-1.5 text-sm text-ink-800"
                  >
                    <span className="tabular-nums text-ink-500">{indice + 1}</span>
                    <span className="tabular-nums">{formatDateTimeValue(fila.DateTime)}</span>
                    <span className="text-right tabular-nums">{fila.Pump}</span>
                    <span className="text-right tabular-nums">{fila.Nozzle}</span>
                    <span className="truncate">{fila.ProductName}</span>
                    <span className="text-right tabular-nums">{galones(fila.Volume)}</span>
                    <span className="text-right tabular-nums">{formatCurrency(fila.Price)}</span>
                    <span className="text-right font-semibold tabular-nums">
                      {formatCurrency(fila.Amount)}
                    </span>
                  </div>
                ))}

                {(datos?.pump_transactions ?? []).length > 500 ? (
                  <p className="py-3 text-center text-xs text-ink-500">
                    Se muestran las primeras 500 transacciones. Exporta el resumen para ver el
                    detalle completo.
                  </p>
                ) : null}
              </div>
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}
